// Global device-join APPROVAL banner. Mounted once on authed pages; polls the
// trusted-device inbox (/auth/join/pending) every 3s so a new-device approval
// prompt appears WITHOUT a manual refresh, with a live countdown that auto-
// dismisses each request at 0. Approving requires typing "OK" to confirm.
//
// Realtime: we drive updates by polling (reliable through the DPoP/bearer auth
// layer). An SSE endpoint (/auth/join/events) also exists server-side for future
// push; native EventSource cannot send our auth headers, so polling is the
// primary, dependable path here.
import { h, icon, toast } from './dom.js';
import { api } from '../api/client.js';
import type { PendingJoin } from '../api/types.js';
import { formatCountdown, secondsRemaining, diffJoinIds } from './join-countdown.js';
export { formatCountdown, secondsRemaining } from './join-countdown.js';

const POLL_MS = 3000;

let mounted = false;
let pollTimer: number | undefined;
let tickTimer: number | undefined;

interface CardState {
  req: PendingJoin;
  confirming: boolean; // Approve pressed → typed-OK input revealed
  busy: boolean;
  draft: string; // in-progress text of the "Type OK" input, preserved across re-renders
}

const state = new Map<string, CardState>();
let container: HTMLElement | null = null;

function deviceLabel(req: PendingJoin): string {
  const ua = req.new_device_ua?.trim();
  const ip = req.new_device_ip?.trim();
  if (ua && ip) return `${ua} · ${ip}`;
  return ua || ip || 'Unknown device';
}

async function approve(joinID: string): Promise<void> {
  const cs = state.get(joinID);
  if (!cs || cs.busy) return;
  cs.busy = true;
  render();
  try {
    await api.auth.joinApprove(joinID, 'trusted-approve');
    toast('Device approved', 'ok');
    state.delete(joinID);
  } catch {
    toast('Approve failed (maybe expired)', 'danger');
    state.delete(joinID);
  }
  render();
  void refresh();
}

async function deny(joinID: string): Promise<void> {
  const cs = state.get(joinID);
  if (!cs || cs.busy) return;
  cs.busy = true;
  render();
  try {
    await api.auth.joinDeny(joinID);
    toast('Device denied', 'warn');
  } catch {
    /* best-effort */
  }
  state.delete(joinID);
  render();
  void refresh();
}

function renderCard(cs: CardState): HTMLElement {
  const req = cs.req;
  const secs = secondsRemaining(req);
  const countdown = h('span', {
    class: 'vid-join-count',
    style: 'font-variant-numeric:tabular-nums;font-weight:800;color:var(--warn-strong,#7a4f01)',
  }, formatCountdown(secs));

  const info = h('div', { style: 'flex:1;min-width:0' },
    h('div', { style: 'font-weight:800;color:var(--warn-strong,#7a4f01)' }, 'New device wants to sign in'),
    h('div', { style: 'font-size:.85rem;overflow:hidden;text-overflow:ellipsis;white-space:nowrap;color:var(--warn-strong,#7a4f01);opacity:.75' }, deviceLabel(req)),
  );

  let actions: HTMLElement;
  if (cs.confirming) {
    const input = h('input', {
      type: 'text', placeholder: 'Type OK', autofocus: true,
      // data-join-input marks this as the focus-preserve target across re-renders.
      'data-join-input': req.join_id,
      style: 'width:90px;padding:6px 8px;border-radius:8px;border:1px solid var(--border,#ccc)',
      oninput: () => { cs.draft = input.value; confirmBtn.disabled = input.value.trim().toUpperCase() !== 'OK'; },
      onkeydown: (e: KeyboardEvent) => { if (e.key === 'Enter' && input.value.trim().toUpperCase() === 'OK') void approve(req.join_id); },
    }) as HTMLInputElement;
    // Restore in-progress text after a re-render so a background poll never wipes it.
    input.value = cs.draft;
    const confirmBtn = h('button', {
      class: 'btn btn-primary btn-sm', disabled: cs.draft.trim().toUpperCase() !== 'OK',
      onclick: () => { if (input.value.trim().toUpperCase() === 'OK') void approve(req.join_id); },
    }, 'Confirm') as HTMLButtonElement;
    actions = h('div', { class: 'row', style: 'gap:8px;align-items:center' }, input, confirmBtn);
  } else {
    actions = h('div', { class: 'row', style: 'gap:8px;align-items:center' },
      h('button', {
        class: 'btn btn-primary btn-sm', disabled: cs.busy,
        onclick: () => { cs.confirming = true; render(); focusJoinInput(req.join_id); },
      }, 'Approve'),
      h('button', {
        class: 'btn btn-sm', disabled: cs.busy,
        onclick: () => void deny(req.join_id),
      }, 'Deny'),
    );
  }

  return h('div', {
    class: 'vid-join-card',
    style: 'display:flex;gap:12px;align-items:center;padding:10px 14px;color:var(--warn-strong,#7a4f01);background:var(--warn-soft,#fff7e6);border:1px solid var(--warn,#e0a800);border-radius:12px;box-shadow:var(--shadow,0 2px 8px rgba(0,0,0,.08))',
  },
    icon('<rect x="3" y="11" width="18" height="10" rx="2"/><path d="M7 11V7a5 5 0 0 1 10 0v4"/>', 20),
    info,
    countdown,
    actions,
  );
}

// focusJoinInput moves focus to a card's "Type OK" input and restores the caret
// to the end. Used after (re)rendering while confirming so a background poll's
// re-render never leaves the user without focus in the field.
function focusJoinInput(joinID: string, caret?: number): void {
  if (!container) return;
  const el = container.querySelector<HTMLInputElement>(`input[data-join-input="${joinID}"]`);
  if (!el) return;
  el.focus();
  const pos = caret ?? el.value.length;
  try { el.setSelectionRange(pos, pos); } catch { /* non-text input types */ }
}

function render(): void {
  if (!container) return;
  // Capture which join input (if any) is focused + its caret, so a re-render
  // triggered by a background poll/tick does not steal focus mid-typing.
  const active = document.activeElement as HTMLInputElement | null;
  const focusedJoinID = active?.getAttribute?.('data-join-input') || null;
  const caret = focusedJoinID ? (active!.selectionStart ?? active!.value.length) : undefined;

  const cards = [...state.values()].map(renderCard);
  if (cards.length === 0) {
    container.style.display = 'none';
    container.replaceChildren();
    return;
  }
  container.style.display = 'flex';
  container.replaceChildren(...cards);

  // Restore focus + caret to the input the user was typing in.
  if (focusedJoinID) focusJoinInput(focusedJoinID, caret);
}

// tick recomputes countdowns each second and evicts requests that hit 0.
function tick(): void {
  let changed = false;
  for (const [id, cs] of state) {
    if (secondsRemaining(cs.req) <= 0) {
      state.delete(id);
      changed = true;
    }
  }
  if (changed) render();
  else {
    // update just the countdown labels in place without a full re-render
    if (!container) return;
    const cards = container.querySelectorAll('.vid-join-card');
    let i = 0;
    for (const cs of state.values()) {
      const el = cards[i++]?.querySelector('.vid-join-count');
      if (el) el.textContent = formatCountdown(secondsRemaining(cs.req));
    }
  }
}

async function refresh(): Promise<void> {
  let requests: PendingJoin[] = [];
  try {
    const res = await api.auth.joinPending();
    requests = res.requests || [];
  } catch {
    return; // transient; keep current state
  }
  const seen = new Set<string>();
  const prevIds = [...state.keys()];
  let structuralChange = false;
  for (const req of requests) {
    seen.add(req.join_id);
    const existing = state.get(req.join_id);
    if (existing) {
      existing.req = req; // refresh device info + expiry (countdown updates via tick())
    } else {
      state.set(req.join_id, { req, confirming: false, busy: false, draft: '' });
      structuralChange = true;
    }
  }
  // Drop requests the server no longer reports (resolved/expired elsewhere).
  for (const id of [...state.keys()]) {
    if (!seen.has(id) && !state.get(id)!.busy) { state.delete(id); structuralChange = true; }
  }
  // Only re-render when the SET of cards changed. A plain poll that merely
  // refreshes existing requests must NOT rebuild the DOM — that was stealing
  // focus from the "Type OK" field every 3s while the user was typing. The 1s
  // tick() already updates countdown labels in place. (busy cards were skipped
  // on delete above, so diff the id sets as the authoritative check.)
  if (structuralChange || diffJoinIds(prevIds, state.keys())) render();
}

/**
 * mountJoinBanner installs the sticky approval banner at the top of the app
 * chrome and starts the 3s poll + 1s countdown ticker. Idempotent: safe to call
 * on every route render. Never mounts on /login or /join.
 */
export function mountJoinBanner(): void {
  const hash = (location.hash || '').replace(/^#/, '');
  if (hash.startsWith('/login') || hash.startsWith('/join')) {
    unmountJoinBanner();
    return;
  }
  if (!container) {
    container = h('div', {
      id: 'vid-join-banner',
      style: 'position:sticky;top:0;z-index:60;display:none;flex-direction:column;gap:8px;padding:10px 16px',
    });
  }
  // Ensure the banner is the first child of the app root each render.
  const app = document.getElementById('app');
  if (app && container.parentElement !== app) {
    app.insertBefore(container, app.firstChild);
  }
  render();

  if (!mounted) {
    mounted = true;
    void refresh();
    pollTimer = window.setInterval(() => void refresh(), POLL_MS);
    tickTimer = window.setInterval(tick, 1000);
  }
}

/** unmountJoinBanner tears down timers + DOM (used on logout / public routes). */
export function unmountJoinBanner(): void {
  if (pollTimer) { clearInterval(pollTimer); pollTimer = undefined; }
  if (tickTimer) { clearInterval(tickTimer); tickTimer = undefined; }
  mounted = false;
  state.clear();
  if (container?.parentElement) container.parentElement.removeChild(container);
  container = null;
}
